import { createContext, useContext, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react'

export type PageHeaderOverride = {
  title?: string
  subtitle?: string
  showBack?: boolean
  onBack?: () => void
  hideAppHeader?: boolean
} | null

type HeaderEntry = {
  id: number
  title?: string
  subtitle?: string
  showBack?: boolean
  onBack?: () => void
  hideAppHeader?: boolean
}

type Api = {
  push: (entry: HeaderEntry) => void
  update: (id: number, patch: Partial<HeaderEntry>) => void
  pop: (id: number) => void
}

const PageHeaderApiContext = createContext<Api | null>(null)

let nextHeaderId = 1

export const PageHeaderProvider = ({
  setOverride,
  children,
}: {
  setOverride: (value: PageHeaderOverride) => void
  children: ReactNode
}) => {
  const stackRef = useRef<HeaderEntry[]>([])

  const api = useMemo<Api>(() => {
    const emit = () => {
      const top = stackRef.current[stackRef.current.length - 1]
      setOverride(top ? {
        title: top.title,
        subtitle: top.subtitle,
        showBack: top.showBack,
        onBack: top.onBack,
        hideAppHeader: top.hideAppHeader,
      } : null)
    }
    return {
      push: (entry) => {
        stackRef.current = [...stackRef.current, entry]
        emit()
      },
      update: (id, patch) => {
        stackRef.current = stackRef.current.map((item) => (item.id === id ? { ...item, ...patch } : item))
        emit()
      },
      pop: (id) => {
        stackRef.current = stackRef.current.filter((item) => item.id !== id)
        emit()
      },
    }
  }, [setOverride])

  return (
    <PageHeaderApiContext.Provider value={api}>
      {children}
    </PageHeaderApiContext.Provider>
  )
}

export function usePageHeader(
  title: string,
  options?: { subtitle?: string; showBack?: boolean; onBack?: () => void; hideAppHeader?: boolean },
) {
  const api = useContext(PageHeaderApiContext)
  const idRef = useRef(0)
  const onBackRef = useRef(options?.onBack)
  onBackRef.current = options?.onBack

  const subtitle = options?.subtitle
  const showBack = options?.showBack
  const hideAppHeader = options?.hideAppHeader
  const hasOnBack = typeof options?.onBack === 'function'

  useLayoutEffect(() => {
    if (!api) return
    if (!idRef.current) idRef.current = nextHeaderId++
    const id = idRef.current
    api.push({
      id,
      title,
      subtitle,
      showBack,
      hideAppHeader,
      onBack: hasOnBack ? () => onBackRef.current?.() : undefined,
    })
    return () => api.pop(id)
  }, [api])

  useLayoutEffect(() => {
    if (!api || !idRef.current) return
    api.update(idRef.current, {
      title,
      subtitle,
      showBack,
      hideAppHeader,
      onBack: hasOnBack ? () => onBackRef.current?.() : undefined,
    })
  }, [api, title, subtitle, showBack, hideAppHeader, hasOnBack])
}

export function AppBarTitle(props: {
  title: string
  subtitle?: string
  showBack?: boolean
  onBack?: () => void
  hideAppHeader?: boolean
}) {
  usePageHeader(props.title, props)
  return null
}
